/**
 * GMAssistantLayout
 *
 * Tabbed container for the GM Assistant popup window.
 * Session Plan is the first (and currently only active) content module.
 * NPCs and Lore tabs are placeholders for future expansion.
 */

import { useState } from 'react';
import { Icon, type IconName } from '../Icon';
import { SessionPlanView } from './SessionPlanView';
import { NPCListView } from './NPCListView';
import { MapExplorerPanel } from '../dm-console/MapExplorerPanel';
import { BestiaryPanel } from '../dm-console/BestiaryPanel';
import { MenageriePanel } from '../dm-console/MenageriePanel';
import { CampaignLorePanel } from './CampaignLorePanel';
import { useCampaignStore } from '../../stores/campaignStore';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';
const SILVER = '#c0c0c0';

type FontSize = 'small' | 'medium' | 'large';
const FONT_SCALE: Record<FontSize, number> = { small: 0, medium: 2, large: 4 };
const LS_KEY = 'machina:gm-assist-font-size';

function getSavedFontSize(): FontSize {
  const v = localStorage.getItem(LS_KEY);
  if (v === 'small' || v === 'medium' || v === 'large') return v;
  return 'medium';
}

type TabId = 'session' | 'npcs' | 'bestiary' | 'menagerie' | 'maps' | 'lore';

interface Tab {
  id: TabId;
  label: string;
  icon: IconName;
}

/** Playsets that have built-in lore content */
const LORE_PLAYSETS = new Set(['tethered_city']);

const BASE_TABS: Tab[] = [
  { id: 'session', label: 'Session Plan', icon: 'scroll' },
  { id: 'npcs', label: 'NPCs', icon: 'users' },
  { id: 'bestiary', label: 'Bestiary', icon: 'skull' },
  { id: 'menagerie', label: 'Menagerie', icon: 'diamond' },
  { id: 'maps', label: 'Maps', icon: 'layers' },
  { id: 'lore', label: 'Lore', icon: 'compass' },
];

export function GMAssistantLayout({ campaignId }: { campaignId: string }) {
  const campaign = useCampaignStore((s) => s.campaigns.find((c) => c.id === campaignId));
  const hasLore = campaign?.playset != null && LORE_PLAYSETS.has(campaign.playset);

  const tabs = hasLore ? BASE_TABS : BASE_TABS.filter((t) => t.id !== 'lore');

  const [activeTab, setActiveTab] = useState<TabId>('session');
  const [fontSize, setFontSize] = useState<FontSize>(getSavedFontSize);

  const handleFontSize = (size: FontSize) => {
    setFontSize(size);
    localStorage.setItem(LS_KEY, size);
  };

  return (
    <div style={styles.container}>
      {/* Tab bar */}
      <div style={styles.tabBar}>
        <div style={styles.tabBarTitle}>
          <Icon name="scroll" size={14} color={GOLD} />
          <span style={{ color: GOLD, fontSize: 12, fontWeight: 'bold', letterSpacing: 1 }}>
            GM ASSISTANT
          </span>
          <div style={{ flex: 1 }} />
          <FontSizeControl current={fontSize} onChange={handleFontSize} />
        </div>
        <div style={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...styles.tab,
                color: activeTab === tab.id ? GOLD : SILVER,
                borderBottomColor: activeTab === tab.id ? GOLD : 'transparent',
                backgroundColor: activeTab === tab.id ? 'rgba(212, 175, 55, 0.08)' : 'transparent',
              }}
            >
              <Icon name={tab.icon} size={12} color={activeTab === tab.id ? GOLD : SILVER} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'session' && <SessionPlanView fontScale={FONT_SCALE[fontSize]} />}
        {activeTab === 'npcs' && <NPCListView campaignId={campaignId} fontScale={FONT_SCALE[fontSize]} />}
        {activeTab === 'bestiary' && <div style={{ padding: 16 }}><BestiaryPanel /></div>}
        {activeTab === 'menagerie' && <div style={{ padding: 16 }}><MenageriePanel /></div>}
        {activeTab === 'maps' && <div style={{ padding: 16 }}><MapExplorerPanel /></div>}
        {activeTab === 'lore' && <CampaignLorePanel fontScale={FONT_SCALE[fontSize]} />}
      </div>
    </div>
  );
}

function FontSizeControl({ current, onChange }: { current: FontSize; onChange: (s: FontSize) => void }) {
  const sizes: FontSize[] = ['small', 'medium', 'large'];
  const labels = { small: 'A', medium: 'A', large: 'A' };
  const fontSizes = { small: 10, medium: 13, large: 16 };

  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
      {sizes.map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          title={`${s} text`}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 4px',
            fontFamily: 'Georgia, serif',
            fontSize: fontSizes[s],
            fontWeight: 'bold',
            color: current === s ? GOLD : 'rgba(192, 192, 192, 0.35)',
            transition: 'color 0.2s',
          }}
        >
          {labels[s]}
        </button>
      ))}
    </div>
  );
}


const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  tabBar: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: `1px solid ${GOLD_DIM}`,
    backgroundColor: 'rgba(20, 15, 25, 0.9)',
    flexShrink: 0,
  },
  tabBarTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px 4px',
  },
  tabs: {
    display: 'flex',
    gap: 2,
    padding: '0 12px',
  },
  tab: {
    padding: '8px 14px',
    border: 'none',
    borderBottom: '2px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'Georgia, serif',
    fontSize: 11,
    letterSpacing: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.2s',
  },
  content: {
    flex: 1,
    overflow: 'auto',
  },
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    height: '100%',
    minHeight: 300,
  },
};
